import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Package, 
  Search, 
  ExternalLink, 
  Calendar,
  Image as ImageIcon,
  Loader2,
  AlertCircle,
  Plus
} from 'lucide-react';
import { getCategoryConfig } from '@/constants/categories';
import type { Product } from '@/types';
import axios from 'axios';
import type { AppStep } from '@/types/app';
import { useProductActions } from '@/hooks/useProductActions';

interface ProductsListPageProps {
  onNavigate: (step: AppStep) => void;
  onViewProduct: (product: Product) => void;
}

interface ApiProduct {
  id: number | string;
  title: string;
  category: string;
  orientation?: string;
  masterImageUrl: string | null;
  printFileUrls: Record<string, string> | null;
  googleDriveLink: string | null;
  finalPdfUrl: string | null;
  bunnyBackupSlug: string;
  mockupUrls: string[];
  createdAt: string;
  updatedAt: string;
}

export function ProductsListPage({ onNavigate, onViewProduct }: ProductsListPageProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 12;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/api/products`, {
        params: { page, limit },
      });

      if (response.data.success) {
        const productData = response.data.data.products.map((p: ApiProduct) => ({
          ...p,
          id: String(p.id),
          createdAt: new Date(p.createdAt),
          updatedAt: new Date(p.updatedAt),
        }));
        
        setProducts(productData);
        setTotal(response.data.data.total);
      } else {
        setError('Failed to load products');
      }
    } catch (err) {
      setError('Failed to load products from server');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const {
    dialogOpen,
    dialogMode,
    selectedProduct,
    newTitle,
    setNewTitle,
    submitting,
    openRenameDialog,
    openDeleteDialog,
    closeDialog,
    confirmDialog,
  } = useProductActions({
    onRefresh: fetchProducts,
  });

  const filteredProducts = useMemo(() => 
    products.filter(product =>
      product.title.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [products, searchQuery]
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Package className="h-4 w-4" />
            <span>Product Library</span>
          </div>
          <h1 className="text-4xl font-bold">All Products</h1>
          <p className="text-muted-foreground mt-2">
            {total} {total === 1 ? 'product' : 'products'} in your library
          </p>
        </div>
        <Button onClick={() => onNavigate('upload')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Product
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products by title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {error && !loading && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
            <Button variant="outline" className="mt-4" onClick={fetchProducts}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && !error && filteredProducts.length === 0 && (
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">
              {searchQuery ? 'No products found' : 'No products yet'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery 
                ? 'Try adjusting your search query' 
                : 'Create your first product to get started'}
            </p>
            {!searchQuery && (
              <Button onClick={() => onNavigate('upload')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Product
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!loading && !error && filteredProducts.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onViewProduct={onViewProduct}
                onDeleteProduct={openDeleteDialog}
                onRenameProduct={openRenameDialog}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'delete' ? 'Delete product' : 'Rename product'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'delete'
                ? `Are you sure you want to delete "${selectedProduct?.title ?? ''}"? This cannot be undone.`
                : 'Update the product title below.'}
            </DialogDescription>
          </DialogHeader>

          {dialogMode === 'rename' && (
            <div className="space-y-2">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="New product title"
                autoFocus
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant={dialogMode === 'delete' ? 'destructive' : 'default'}
              onClick={confirmDialog}
              disabled={submitting || (dialogMode === 'rename' && !newTitle.trim())}
            >
              {dialogMode === 'delete' ? 'Delete' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ProductCardProps {
  product: Product;
  onViewProduct: (product: Product) => void;
  onDeleteProduct: (product: Product) => void;
  onRenameProduct: (product: Product) => void;
}

function ProductCard({ product, onViewProduct, onDeleteProduct, onRenameProduct }: ProductCardProps) {
  const categoryConfig = getCategoryConfig(product.category);
  const previewImage = product.printFileUrls?.['3:4'] || product.masterImageUrl;
  
  const hasGoogleDrive = !!product.googleDriveLink;
  const hasPDF = !!product.finalPdfUrl;
  const hasMockups = product.mockupUrls && product.mockupUrls.length > 0;
  const hasPrintFiles = product.printFileUrls && Object.keys(product.printFileUrls).length > 0;

  let completionCount = 0;
  if (hasPrintFiles) completionCount++;
  if (hasGoogleDrive) completionCount++;
  if (hasPDF) completionCount++;
  if (hasMockups) completionCount++;
  const completionPercent = (completionCount / 4) * 100;

  const getStatusBadge = () => {
    if (completionPercent === 100) {
      return <Badge className="bg-green-600">Complete</Badge>;
    } else if (completionPercent >= 50) {
      return <Badge className="bg-yellow-600">In Progress</Badge>;
    } else {
      return <Badge variant="secondary">Draft</Badge>;
    }
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group">
      <div onClick={() => onViewProduct(product)}>
        <div className="relative aspect-[3/4] bg-muted overflow-hidden">
          {previewImage ? (
            <img
              src={previewImage}
              alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="h-12 w-12 text-muted-foreground" />
            </div>
          )}
          
          <div className="absolute top-3 right-3">
            {getStatusBadge()}
          </div>
        </div>

        <CardHeader>
          <CardTitle className="line-clamp-2 text-lg">{product.title}</CardTitle>
          <CardDescription className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {categoryConfig.label}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {product.orientation === 'landscape' ? 'Landscape' : 'Portrait'}
            </Badge>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Completion</span>
              <span>{completionPercent}%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 text-muted-foreground">
              <ImageIcon className="h-3 w-3" />
              <span>{hasPrintFiles ? Object.keys(product.printFileUrls!).length : 0} prints</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{product.createdAt.toLocaleDateString()}</span>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                onViewProduct(product);
              }}
            >
              View Details
            </Button>
            {product.googleDriveLink && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(product.googleDriveLink!, '_blank');
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRenameProduct(product);
              }}
            >
              Rename
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteProduct(product);
              }}
            >
              Delete
            </Button>
          </div>
        </CardContent>
      </div>
    </Card>
  );
}
